import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Users,
  Briefcase,
  Target,
  TrendingUp,
  CheckCircle,
  Play,
  Star,
  Brain,
  Cloud,
  Lock,
  Smile,
  UserPlus,
  CalendarCheck,
  BookOpen,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import talentImage from "../components/assets/talent-image-D19LLiFJ.jpg";
// Add slider images
import HomeSlider1 from "../components/assets/about-image-3-CfFyGLWa.jpg";
import HomeSlider2 from "../components/assets/cloud-first.jpg";
import HomeSlider3 from "../components/assets/digital-first.jpg";
import HomeSlider4 from "../components/assets/ai.jpeg";
import HomeSlider5 from "../components/assets/edge3.jpg";
import TypewriterEffect from "./TypewriterEffect";
import CloudFirstPage from "./CloudFirstPage";
import CloudHeroSlide from "../components/hero/CloudHeroSlide";
import DigitalFirstPage from "./DigitalFirstPage";
import DigitalHeroSlide from "../components/hero/DigitalHeroSlide";
import AIPoweredPage from "./AIPoweredPage";
import AiHeroSlide from "../components/hero/AiHeroSlide";
import EdgeHeroSlide from "../components/hero/EdgeHeroSlide";
import EdgeReadyPage from "./EdgeReadyPage";

const HomePage = () => {
  const features = [
    {
      icon: Brain,
      title: "Elite Engineers",
      description: "Only the top 1% of remote tech talent globally.",
      href: "/talent-acquisition",
    },
    {
      icon: Cloud,
      title: "Scalable Teams",
      description: "Build flexible, fast, and scalable agile squads.",
      href: "/hr-consulting",
    },
    {
      icon: Lock,
      title: "Secure Solutions",
      description: "Compliance and security at every step.",
      href: "/contract-staffing",
    },
    {
      icon: Users,
      title: "Dedicated Support",
      description: "Your success is our mission from day one.",
      href: "/tech-training",
    },
  ];

  const stats = [
    { number: "500+", label: "Projects Delivered" },
    { number: "98%", label: "Client Satisfaction" },
    { number: "15+", label: "Years Experience" },
    { number: "50+", label: "Tech Partners" },
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "HR Director",
      company: "TechCorp Inc.",
      content:
        "TechMind transformed our recruitment process. We found exceptional talent in record time.",
      rating: 5,
    },
    {
      name: "Michael Chen",
      role: "CEO",
      company: "StartupXYZ",
      content:
        "Their HR consulting services helped us scale efficiently while maintaining our company culture.",
      rating: 5,
    },
    {
      name: "Emily Rodriguez",
      role: "Talent Manager",
      company: "Global Solutions",
      content:
        "The contract staffing solutions provided us with the flexibility we needed for our projects.",
      rating: 5,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  // Slider state
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderImages = [
    { img: HomeSlider1, component: "" , hero:''},
    { img: HomeSlider2, component: CloudFirstPage, hero:CloudHeroSlide },
    { img: HomeSlider3, component: DigitalFirstPage, hero: DigitalHeroSlide },
    { img: HomeSlider4, component: AIPoweredPage, hero:AiHeroSlide },
    { img: HomeSlider5, component: EdgeReadyPage, hero:EdgeHeroSlide },
  ];

  const goToPrev = () =>
    setCurrentSlide((prev) =>
      prev === 0 ? sliderImages.length - 1 : prev - 1
    );
  const goToNext = () =>
    setCurrentSlide((prev) =>
      prev === sliderImages.length - 1 ? 0 : prev + 1
    );

  // Auto-advance slider every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) =>
        prev === sliderImages.length - 1 ? 0 : prev + 1
      );
    }, 10000);
    return () => clearInterval(interval);
  }, [sliderImages.length]);

  // New state for dual tab buttons
  const [activeTab, setActiveTab] = useState("hire"); // 'hire' or 'jobs'

  const CurrentComponent = sliderImages[currentSlide].component;
  const CurrentHeroContent = sliderImages[currentSlide].hero;
  return (
    <div className="min-h-screen7">
      {/* Hero Section with Slider */}
      <section className="relative bg-[#1A193B] h-[716px] text-white overflow-hidden pt-24">
        <div className="absolute inset-0 bg-black/20 z-10"></div>
        {/* Slider Images */}
        <div className="absolute inset-0 w-full h-full flex transition-all duration-700 z-0">
          {sliderImages.map((img, idx) => (
            <img
              key={idx}
              src={img.img}
              alt="Home Slide"
              className={`object-cover w-full h-full absolute top-0 left-0 transition-opacity duration-700 ${
                currentSlide === idx ? "opacity-60 z-20" : "opacity-0 z-0"
              }`}
              style={{ transition: "opacity 0.7s" }}
            />
          ))}
        </div>
        {/* Slider Controls */}
        <div className="absolute left-0 right-0 flex justify-between items-center h-full px-4 z-30 pointer-events-none">
          <button
            onClick={goToPrev}
            className="bg-black/40 hover:bg-black/60 text-white rounded-full p-2 pointer-events-auto"
            aria-label="Previous Slide"
            style={{ top: "50%", transform: "translateY(-50%)" }}
          >
            <ChevronLeft className="w-7 h-7" />
          </button>
          <button
            onClick={goToNext}
            className="bg-black/40 hover:bg-black/60 text-white rounded-full p-2 pointer-events-auto"
            aria-label="Next Slide"
            style={{ top: "50%", transform: "translateY(-50%)" }}
          >
            <ChevronRight className="w-7 h-7" />
          </button>
        </div>
        {/* Dots */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-30" style={{zIndex:'42'}}>
          {sliderImages.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              className={`w-3 h-3 rounded-full ${
                currentSlide === idx ? "bg-white" : "bg-white/40"
              } border border-white transition-all`}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
        {CurrentHeroContent ? (
        <>
        <CurrentHeroContent from={'Home'}/>
        </>
      ) : (
        <>
        {/* Hero Content */}
        <div className="relative flex flex-col items-center max-w-7xl mx-auto px-8 sm:px-6 lg:px-8 py-28 sm:py-24 md:py-28 lg:pt-20 lg:pb-32 z-40">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="w-full text-center"
          >
            <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold leading-tight mb-6">
              Tech <span className="text-blue-300">Jobs,</span>
              <br className="sm:block" />
              Reimagined
            </h1>

            <p className="text-lg sm:text-xl text-gray-200 mb-8 leading-relaxed">
              <TypewriterEffect
                phrases={[
                  "With Expert Engineers",
                  "With Innovation",
                  "With Agile Team",
                ]}
              />
            </p>

            {/* Button Group */}
            <div className="flex flex-row sm:flex-row gap-4 mb-10 justify-center items-center">
              <button
                onClick={() => setActiveTab("hire")}
                className={`inline-flex items-center justify-center px-8 py-3 sm:px-12 sm:py-4 rounded-xl text-base sm:text-xl font-semibold transition-all duration-200 focus:outline-none ${
                  activeTab === "hire"
                    ? "bg-gradient-hero text-white shadow-lg"
                    : "bg-[#E5E7EB] text-black"
                }`}
              >
                Hire Talent
              </button>
              <button
                onClick={() => setActiveTab("jobs")}
                className={`inline-flex items-center justify-center px-8 py-3 sm:px-12 sm:py-4 rounded-xl text-base sm:text-xl font-semibold transition-all duration-200 focus:outline-none border ${
                  activeTab === "jobs"
                    ? "bg-gradient-hero text-white shadow-lg"
                    : "bg-[#E5E7EB] text-black"
                }`}
              >
                Find Jobs
              </button>
            </div>

            {/* Contextual Content */}
            <div className="flex justify-center w-full px-4 sm:px-0 mt-4">
              <div className="w-full max-w-xl sm:max-w-3xl bg-gray-800/70 rounded-lg backdrop-blur-md shadow-lg border border-gray-600/50 flex flex-col sm:flex-row items-center justify-between px-6 sm:px-8 py-6 sm:py-8 shadow-xl gap-4 sm:gap-0">
                <div className="flex-1 text-center sm:text-left">
                  <h2 className="text-base sm:text-lg font-semibold text-white mb-2">
                    {activeTab === "hire"
                      ? "Need specialized tech talent?"
                      : "Looking for your next tech opportunity?"}
                  </h2>
                  <p className="text-sm text-gray-300">
                    {activeTab === "hire"
                      ? "Submit your job requirement now and get matched with the right candidates."
                      : "Browse our open positions and find the perfect role for your skills."}
                  </p>
                </div>
                <Link to={activeTab === "hire" ? "/submit-job" : "/careers"}>
                  <button className="mt-4 sm:mt-0 px-5 py-3 rounded-lg font-medium text-sm text-white bg-gray-700/80 hover:bg-gray-600/90 border border-gray-500/50 hover:border-gray-400/50 whitespace-nowrap transition-all duration-300">
                    {activeTab === "hire" ? "Submit a Job" : "Browse Jobs"}
                  </button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
        </>
      )}
      </section>

      {CurrentComponent ? (
        <>
        <CurrentComponent from={'Home'}/>
        </>
      ) : (
        <>
          {/* Stats Section */}
          <section className="py-16 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="grid grid-cols-2 lg:grid-cols-4 gap-8"
              >
                {stats.map((stat, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    className="text-center"
                  >
                    <div className="text-3xl lg:text-4xl font-bold text-blue-600 mb-2">
                      {stat.number}
                    </div>
                    <div className="text-gray-600 font-medium">
                      {stat.label}
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>

          {/* Features Section */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-center mb-16"
              >
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                  {/* Our Core Services */}
                  Why Choose TechMind Infosys?
                </h2>
                {/* <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive solutions designed to meet all your talent and HR needs
            </p> */}
              </motion.div>

              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
              >
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    className="group"
                  >
                    {/* <Link to={feature.href} className="block"> */}
                    <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group-hover:border-blue-200">
                      <div className="w-16 h-16 bg-[#F9FAFB] rounded-lg flex items-center justify-center mb-6">
                        <feature.icon className="w-8 h-8 text-black  transition-colors duration-300" />
                      </div>
                      <h3 className="text-xl font-semibold mt-5 mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-gray-600">
                        {feature.description}
                      </p>
                    </div>
                    {/* </Link> */}
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>

          {/* AI + Human Expertise Section */}
          <section className=" md:block py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4 }}
                >
                  <img
                    src={talentImage}
                    alt="Professional consultant"
                    className="w-full h-auto rounded-2xl shadow-lg max-w-full object-cover"
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                    Smarter Hiring with AI + Human Expertise
                  </h2>
                  <p className="text-xl text-gray-600 leading-relaxed">
                    Our platform uses AI to shortlist candidates, but every hire
                    is curated by real experts.
                  </p>
                </motion.div>
              </div>
            </div>
          </section>

          {/* How It Works Section */}
          <section className="py-20 bg-gray-900 text-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-center mb-16"
              >
                <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                  How It Works
                </h2>
              </motion.div>

              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
              >
                <motion.div variants={itemVariants} className="text-center">
                  <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-gray-700">
                    <Smile className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Submit a Brief</h3>
                </motion.div>

                <motion.div variants={itemVariants} className="text-center">
                  <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-gray-700">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Get Matched</h3>
                </motion.div>

                <motion.div variants={itemVariants} className="text-center">
                  <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-gray-700">
                    <UserPlus className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">
                    Interview & Select
                  </h3>
                </motion.div>

                <motion.div variants={itemVariants} className="text-center">
                  <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-gray-700">
                    <CalendarCheck className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Start Building</h3>
                </motion.div>
              </motion.div>
            </div>
          </section>
          {/* CTA Section */}
          <section className="py-24 bg-[#1A193B] text-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                  Ready to Transform Your Tech Journey?
                </h2>
                <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                  Whether you're hiring elite developers or building teams,
                  TechMind makes it seamless.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/submit-job"
                    className="inline-flex items-center px-8 py-4 bg-white text-blue-600 hover:bg-gray-100 rounded-lg font-semibold transition-colors duration-200"
                  >
                    Let's Get Started
                    {/* <ArrowRight className="ml-2 w-5 h-5" /> */}
                  </Link>
                  {/* <Link
                to="/about"
                className="inline-flex items-center px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-blue-600 rounded-lg font-semibold transition-colors duration-200"
              >
                Learn More
              </Link> */}
                </div>
              </motion.div>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default HomePage;
